// French copy, namespace `dashboard` (source language: English is typed against it).
// The overview: watch card, pass summary, agent radar, activity and pipeline.

const s = (count: number) => (count > 1 ? "s" : "");

const signals = (count: number) => `${count} ${count > 1 ? "signaux" : "signal"}`;

/** « il y a 3 jours », « aujourd’hui » : la distance compte plus que la date. */
const distance = (days: number) => {
  if (days <= 0) return "aujourd’hui";
  if (days === 1) return "hier";
  if (days < 7) return `il y a ${days} jours`;
  const weeks = Math.floor(days / 7);
  return weeks === 1 ? "il y a une semaine" : `il y a ${weeks} semaines`;
};

export const dashboard = {
  page: {
    title: "Votre espace — Hune",
    subject: "votre vue d’ensemble",
    heading: "Tableau de bord",
  },
  readOnly: {
    before: "Vous consultez l’espace client ",
    after: " en lecture seule. Repassez sur « Mon espace » pour lancer un passage.",
  },
  activity: {
    title: (days: number) => `Activité des ${days} derniers jours`,
    comparison: (days: number) => `Comparée aux ${days} jours précédents`,
    passes: "Passages",
    passesKept: "Conservés avec leurs sources",
    passesEmpty: "Votre historique apparaîtra ici",
    opportunities: "Opportunités relevées",
    sources: (count: number) => `${count} source${s(count)} distincte${s(count)}`,
    sourcesEmpty: "Chaque signal reste consultable",
    leads: "Leads entrés en mémoire",
    pipelineTotal: (count: number) => `${count} au total dans le pipeline`,
    personalOnly: "Espace personnel uniquement",
  },
  statTile: {
    stable: "stable",
    versus: (delta: string, days: number) => `${delta} vs ${days} j préc.`,
    comparedTo: (days: number) => `Par rapport aux ${days} jours précédents`,
  },
  runs: {
    title: "Derniers passages",
    history: "Tout l’historique",
    empty: "Aucun passage enregistré pour l’instant.",
    launchFirst: "Lancer le premier",
    noOpportunity: "Aucune opportunité",
    opportunities: (count: number) => `${count} opportunité${s(count)}`,
    /** How a pass started, keyed by `research_runs.source`. */
    sources: { radar: "veille", manual: "manuel" },
  },
  pipeline: {
    title: "Pipeline",
    open: "Ouvrir",
    personalOnly: "Le pipeline s’affiche depuis votre espace personnel.",
    empty: "Aucun lead pour l’instant. Ils arriveront avec votre prochaine analyse.",
    /** Keyed by `PIPELINE_STAGES` (plus `other`, the rest of the pipeline). */
    stages: {
      fresh: "Non ouverts",
      toContact: "À contacter",
      engaged: "En conversation",
      won: "Gagnés",
      other: "Autres",
    },
    stageCount: (count: number, stage: string) => `${count} ${stage.toLowerCase()}`,
    breakdown: (total: number, parts: string[]) => `Répartition de ${total} leads : ${parts.join(", ")}.`,
    tracked: (count: number) => `${count} leads suivis`,
    openUnread: (count: number) => `Ouvrir les ${count} non lus`,
  },
  passSummary: {
    label: "Bilan du dernier passage",
    completed: "Passage terminé",
    last: "Dernier passage",
    details: "Le passage en détail",
    newLeads: (count: number): string => (count > 1 ? "nouveaux leads" : "nouveau lead"),
    reactivated: (count: number): string => (count > 1 ? "réactivés" : "réactivé"),
    known: (count: number): string => (count > 1 ? "déjà connus" : "déjà connu"),
    nothingNew: (count: number) =>
      `${signals(count)} relevé${s(count)}, aucun nouveau lead : le marché n’a rien émis de neuf depuis le passage précédent.`,
    sourced: (count: number) =>
      `${signals(count)} sourcé${s(count)} par ce passage, déjà rattaché${s(count)} aux fiches du pipeline.`,
  },
  watch: {
    errors: {
      generic: "Une erreur est survenue.",
      follow: "Impossible de suivre le passage.",
      failed: "Le passage a échoué.",
      tooLong: "Le passage prend plus de temps que prévu. Le rapport apparaîtra dans vos passages.",
      start: "Impossible de démarrer le passage.",
      noReport: "L’agent n’a pas retourné de rapport.",
      saveWatch: "Impossible d’enregistrer la veille.",
    },
    status: {
      busy: { title: "Passage en cours", note: "l’agent explore votre marché" },
      error: { title: "Passage interrompu", note: "vous pouvez relancer" },
      outOfPlan: "Hors de votre plan",
      /** `cadence`: `cadenceLabel(days, locale)` (« chaque semaine »). */
      active: { title: "Veille active", note: (cadence: string) => `passage ${cadence}` },
      paused: { title: "Veille en pause", note: "aucun passage automatique" },
      available: { title: "Agent disponible", note: "prêt pour la première analyse" },
    },
    distance,
    lastPass: (days: number, nextDay: string | null) =>
      `Dernier passage ${distance(days)}${nextDay ? ` · prochain le ${nextDay}` : ""}`,
    firstPass: (day: string) => `Premier passage le ${day}`,
    noPass: "Aucun passage encore",
    toggle: "Veille automatique",
    unread: (count: number) => `Voir ${count} lead${s(count)} non lu${s(count)}`,
    openPipeline: "Ouvrir le pipeline",
    cooldownTitle: (hours: number) => `Un passage vient d’avoir lieu — relance possible dans ${hours} h`,
    searchNowTitle: "Lancer un passage maintenant",
    running: "Passage en cours",
    searchNow: "Chercher maintenant",
    offersTitle: "Ciblage, cadence et offres surveillées",
    offers: "Mes offres",
    first: {
      label: "Première analyse",
      intro:
        "Indiquez l’adresse de votre site : l’agent en déduit votre offre et votre cible, puis surveille le marché à votre place. Aucune source n’est inventée, aucun message n’est envoyé, un passage dure 5 à 10 minutes.",
      placeholder: "votre-produit.fr",
      analyzing: "Analyse en cours",
      launch: "Lancer la première analyse",
      demo: "Voir une démonstration",
    },
    background:
      "Vous pouvez changer d’onglet ou le fermer : le passage continue et ses leads vous attendront dans le pipeline.",
    /** `days`: age of the last pass, `null` when unknown. */
    cooldown: (days: number | null, hours: number) =>
      `Un passage a eu lieu ${days === null ? "récemment" : distance(days)} : l’agent ne re-signale jamais un fait déjà connu, il ne trouverait rien de neuf avant ${hours} h.`,
    searchAnyway: "Chercher quand même",
  },
  notifyMe: {
    enabled: "Notification activée pour la fin du passage.",
    ask: "Me prévenir à la fin",
  },
  radar: {
    /** The eight branches of the radar, keyed by `RADAR_FAMILIES`. */
    families: {
      network: "Réseau pro",
      hiring: "Emploi",
      procurement: "Appels d’offres",
      press: "Presse",
      registries: "Registres",
      regulation: "Réglementation",
      events: "Événements",
      communities: "Communautés",
    },
    label: (searches: number, pages: number) =>
      `Sources explorées par l’agent : ${searches} recherches, ${pages} pages lues`,
    pages: (count: number) => `${count} page${s(count)}`,
    searches: (count: number) => `${count} recherche${s(count)}`,
    now: {
      connecting: "Connexion à l’agent…",
      queued: "En file d’attente : l’agent démarre dans un instant",
      understand: "Lecture de votre offre et de votre marché",
      searching: (query: string) => `Cherche « ${query} »`,
      reading: (host: string) => `Lit ${host}`,
      exploring: "Explore les sources",
      filing: "Range les leads dans votre pipeline",
      writing: "Recoupe les sources et rédige le rapport",
      contacts: "Cherche les bons interlocuteurs",
      leads: (count: number) => `${count} lead${s(count)} nouveau${count > 1 ? "x" : ""} ou réactivé${s(count)}`,
      nothingNew: "Passage terminé : rien de nouveau cette fois",
      failed: "Le passage a été interrompu.",
    },
    steps: {
      understand: "Comprendre l’offre",
      explore: "Explorer les sources",
      retain: "Retenir les signaux",
      contacts: "Trouver les interlocuteurs",
    },
    notes: {
      queued: "en file d’attente",
      retained: (count: number) => `${count} retenu${s(count)}`,
      writing: "rédaction",
      enriched: (done: number, total: number) => `${done} sur ${total}`,
    },
    totals: { searches: "recherches", pages: "pages lues", leads: "leads" },
    current: "En ce moment",
    outcome: "Bilan",
  },
  passLeads: {
    title: "Leads issus de ce passage",
    openPipeline: "Ouvrir le pipeline",
  },
  errors: {
    signIn: "Connectez-vous.",
    invalidId: "Identifiant invalide.",
    signInToFollow: "Connectez-vous pour suivre un passage.",
    passNotFound: "Passage introuvable.",
  },
};
